import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import AddHoldingDialog from "@/components/AddHoldingDialog";
import PortfolioChart from "@/components/PortfolioChart";
import DividendCard from "@/components/DividendCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, TrendingUp, TrendingDown, Wallet, LineChart, Layers, Trash2, Sparkles, ArrowUpRight, Loader2, Newspaper, Activity, Star, ShieldCheck, BookOpen, Home, Briefcase, BarChart3, Radar, Building2, Wrench, Search, Trophy, Users, GraduationCap, Rocket, Coins, Crown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { fmtPct, fmtSigned, fmtPrice, trendColor } from "@/utils/format";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

const COLORS = ["#1D4ED8", "#DC2626", "#60A5FA", "#F87171", "#0B2A6F", "#93C5FD", "#991B1B", "#3B82F6"];

export default function Dashboard() {
  const { user, openAuth } = useAuth();
  const { openStockDetail } = useModals();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [currency, setCurrency] = useState(() => localStorage.getItem("portfolio_ccy") || "CAD");
  useEffect(() => localStorage.setItem("portfolio_ccy", currency), [currency]);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    api.get("/portfolio").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const remove = async (id, symbol) => {
    try {
      const { data } = await api.delete(`/portfolio/${id}`);
      setData(data);
      toast.success(`Removed ${symbol}`);
    } catch {
      toast.error("Failed to remove");
    }
  };

  if (!user) return <SignedOutHero openAuth={openAuth} />;

  const s = currency === "USD" ? data?.summary_usd : data?.summary_cad || data?.summary;
  const holdings = data?.holdings || [];
  const pie = holdings.map((h) => ({ name: h.symbol, value: currency === "USD" ? h.market_value_usd : h.market_value_cad }));
  const sym = currency === "USD" ? "US$" : "C$";
  const money = (n) => `${sym}${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const signed = (n) => `${Number(n) >= 0 ? "+" : "-"}${money(Math.abs(n))}`;

  return (
    <div data-testid="portfolio-tracker-section" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Portfolio</h1>
          <p className="text-muted-foreground mt-1">
            Your holdings, live P/L and allocation.
            {data?.fx && <span className="font-num text-xs"> · 1 US$ = C${data.fx.usd_cad}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border bg-card p-0.5" data-testid="currency-toggle">
            {["CAD", "USD"].map((c) => (
              <button key={c} data-testid={`currency-${c}`} onClick={() => setCurrency(c)} className={`px-3 py-1.5 rounded-md text-xs font-num font-semibold transition-colors ${currency === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{c}</button>
            ))}
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-holding-btn">
            <Plus className="w-4 h-4 mr-2" /> Add holding
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="h-64 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : holdings.length === 0 ? (
        <EmptyPortfolio onAdd={() => setAddOpen(true)} />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="stat-total-value" icon={Wallet} label={`Total Value (${currency})`} value={money(s.total_value)} />
            <StatCard testid="stat-total-gain" icon={TrendingUp} label="Total Gain / Loss"
              value={signed(s.total_gain)} sub={fmtPct(s.total_gain_percent)} tone={s.total_gain} />
            <StatCard testid="stat-day-change" icon={LineChart} label="Day Change"
              value={signed(s.day_change)} sub={fmtPct(s.day_change_percent)} tone={s.day_change} />
            <StatCard testid="stat-invested" icon={Layers} label={`Invested (${currency})`} value={money(s.total_cost)} />
          </div>

          <PortfolioChart refreshKey={holdings.map((h) => h.id).join(",")} currency={currency} />

          <DividendCard refreshKey={holdings.map((h) => h.id).join(",")} currency={currency} />


          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-xl border border-border bg-card overflow-hidden">
              <div className="px-5 py-4 border-b border-border font-heading font-semibold">Holdings</div>
              <div className="divide-y divide-border">
                {holdings.map((h) => (
                  <div key={h.id} data-testid={`holding-row-${h.symbol}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/40 transition-colors">
                    <button onClick={() => openStockDetail(h.symbol)} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-bold">{h.symbol}</span>
                        <Badge variant="secondary" className="text-[9px]">{h.exchange}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground font-num">
                        {h.shares} sh · avg {fmtPrice(h.avg_price)}
                      </div>
                    </button>
                    <div className="text-right">
                      <div className="font-num font-semibold">{h.currency === "CAD" ? "C$" : "US$"}{fmtPrice(h.market_value)}</div>
                      <div className={`font-num text-xs ${trendColor(h.gain)}`}>
                        {fmtSigned(h.gain)} ({fmtPct(h.gain_percent)})
                      </div>
                      {h.currency !== currency && (
                        <div className="font-num text-[10px] text-muted-foreground" data-testid={`holding-converted-${h.symbol}`}>≈ {money(currency === "USD" ? h.market_value_usd : h.market_value_cad)}</div>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600" data-testid={`remove-holding-${h.symbol}`} onClick={() => remove(h.id, h.symbol)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <div className="font-heading font-semibold mb-2">Allocation</div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {pie.map((seg, i) => (
                        <Cell key={seg.name} fill={COLORS[i % COLORS.length]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#0B1934", border: "1px solid rgba(255,255,255,0.12)", color: "#F8FAFC", borderRadius: 10, fontSize: 12 }}
                      formatter={(v, n) => [money(v), n]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {pie.map((p, i) => (
                  <div key={p.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                      {p.name}
                    </span>
                    <span className="font-num text-muted-foreground">
                      {((p.value / s.total_value) * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      <AddHoldingDialog open={addOpen} onOpenChange={setAddOpen} onAdded={setData} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`font-num text-2xl font-bold mt-2 ${tone != null ? trendColor(tone) : ""}`}>{value}</div>
      {sub != null && <div className={`font-num text-sm ${trendColor(tone)}`}>{sub}</div>}
    </div>
  );
}

function EmptyPortfolio({ onAdd }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 grid place-items-center text-center">
      <Layers className="w-10 h-10 text-muted-foreground mb-3" />
      <h3 className="font-heading font-semibold text-lg">No holdings yet</h3>
      <p className="text-muted-foreground text-sm mb-4 max-w-sm">Add your first position to start tracking live gains and allocation.</p>
      <Button onClick={onAdd} data-testid="empty-add-holding-btn"><Plus className="w-4 h-4 mr-2" /> Add holding</Button>
    </div>
  );
}

function Sparkline({ up = true, className = "" }) {
  const color = up ? "#10B981" : "#EF4444";
  const path = up
    ? "M0 46 L14 40 L28 43 L42 33 L56 36 L70 26 L84 30 L98 18 L112 22 L126 10 L140 6"
    : "M0 8 L14 14 L28 11 L42 20 L56 17 L70 27 L84 24 L98 34 L112 31 L126 42 L140 46";
  const area = up
    ? path + " L140 56 L0 56 Z"
    : path + " L140 56 L0 56 Z";
  const gid = `spark-${up ? "up" : "down"}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <svg viewBox="0 0 140 56" preserveAspectRatio="none" className={`w-full h-full ${className}`} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SignedOutHero({ openAuth }) {
  const navigate = useNavigate();
  const startTrading = () => openAuth("register");
  const viewLeaderboard = () => navigate("/practice?tab=leaderboard");
  const createTeam = () => navigate("/practice?tab=groups");

  const features = [
    { icon: Coins, tint: "text-[#10B981] bg-[#10B981]/15", title: "$150,000 in Virtual Funds", desc: "Begin practising with $150,000 in virtual funds — without risking any real money." },
    { icon: GraduationCap, tint: "text-[#2F80ED] bg-[#2F80ED]/15", title: "Learn by Practising", desc: "Explore the stock market, test trading strategies, and develop your investment knowledge." },
    { icon: Users, tint: "text-[#A855F7] bg-[#A855F7]/15", title: "Compete With Your Team", desc: "Challenge other participants and represent your school, club, or family." },
    { icon: Trophy, tint: "text-[#F59E0B] bg-[#F59E0B]/15", title: "Climb the Leaderboard", desc: "Track your ranking and challenge yourself to reach the number one spot." },
  ];
  const teams = [
    { icon: GraduationCap, emoji: "🏫", title: "Represent Your School", desc: "Rally classmates and put your school on top of the board." },
    { icon: Users, emoji: "👥", title: "Represent Your Club", desc: "Turn your investing club into a trading powerhouse." },
    { icon: Home, emoji: "🏆", title: "Represent Your Family", desc: "Make trading a friendly family rivalry at the dinner table." },
  ];

  const [liveIdx, setLiveIdx] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () =>
      api.get("/market/overview").then((r) => {
        if (!active) return;
        const m = {};
        (r.data.items || []).filter((i) => i.kind === "index").forEach((i) => { m[i.name] = i; });
        setLiveIdx(m);
      }).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const FALLBACK = {
    "TSX Composite": { value: "35,801.39", chg: "+94.39", pct: "+0.26%", up: true },
    "NASDAQ": { value: "27,069.21", chg: "+129.35", pct: "+0.48%", up: true },
    "S&P 500": { value: "5,688.20", chg: "+32.10", pct: "+0.57%", up: true },
    "Dow Jones": { value: "41,240.52", chg: "-98.14", pct: "-0.24%", up: false },
  };
  const fmtIdx = (n) => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const indices = ["TSX Composite", "NASDAQ", "S&P 500", "Dow Jones"].map((name) => {
    const it = liveIdx && liveIdx[name];
    if (it) {
      const up = it.change_percent >= 0;
      return { name, value: fmtIdx(it.price), chg: `${it.change >= 0 ? "+" : ""}${fmtIdx(it.change)}`, pct: `${it.change_percent >= 0 ? "+" : ""}${it.change_percent}%`, up };
    }
    return { name, ...FALLBACK[name] };
  });
  const featured = indices[0];

  return (
    <div className="space-y-14 sm:space-y-20">
      {/* HERO */}
      <section
        data-testid="signed-out-hero"
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#08152B] text-slate-100 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]"
      >
        <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#2F80ED]/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute bottom-0 right-0 w-[380px] h-[380px] rounded-full bg-[#10B981]/15 blur-3xl" aria-hidden="true" />
        <div className="relative grid md:grid-cols-12 gap-8 md:gap-6 lg:gap-8 items-center px-5 sm:px-10 lg:px-12 py-10 sm:py-14">
          {/* Left copy */}
          <div className="md:col-span-6 min-w-0 reveal">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 px-3.5 py-1.5 text-[11px] font-num uppercase tracking-[0.16em] text-[#34D399]">
              <Coins className="w-3.5 h-3.5" /> $150,000 Virtual Funds · Compete · Climb
            </div>
            <h1 className="font-heading font-extrabold tracking-tight leading-[1.05] mt-5 text-4xl sm:text-5xl lg:text-[3.5rem]">
              Your strategy. Your team.<br />
              Your chance to be <span className="text-[#10B981]">number one!</span>
            </h1>
            <p className="font-heading font-bold text-[#5AA0F2] text-xl sm:text-2xl mt-4">
              Start with $150,000 in Virtual Trading Funds!
            </p>
            <p className="text-slate-300 text-base mt-4 max-w-xl leading-relaxed hidden sm:block">
              Think you have what it takes to become a top trader? Put your investment knowledge to the test with Stewart Capital Finance. Practise trading with $150,000 in virtual funds, develop your strategy, build a virtual portfolio, and compete with other participants.
            </p>
            <p className="text-slate-300 text-base mt-3 max-w-xl leading-relaxed hidden sm:block">
              Represent your school, your club, or your family. Climb the leaderboard and challenge yourself to reach the number one spot!
            </p>
            <p className="text-slate-300 text-base mt-4 max-w-xl leading-relaxed sm:hidden">
              Practise with $150,000 in virtual funds and compete to top the leaderboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button
                size="lg"
                onClick={startTrading}
                data-testid="hero-start-trading-btn"
                className="rounded-full bg-[#10B981] hover:bg-[#0EA371] text-white text-base font-bold px-7 h-13 py-3.5 shadow-lg shadow-[#10B981]/30 transition-colors"
              >
                🚀 Start Practice Trading <ArrowUpRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={viewLeaderboard}
                data-testid="hero-view-leaderboard-btn"
                className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 text-base font-semibold px-7 h-13 py-3.5"
              >
                🏆 View the Leaderboard
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-6 text-xs text-slate-400 font-num">
              <ShieldCheck className="w-4 h-4 text-[#10B981]" /> Virtual funds only — no real money, no risk · Free to start
            </div>

            <a
              href="https://www.amazon.ca/Intelligent-Investor-Third-Investing-Financial/dp/0063497476/ref=sr_1_1?dib=eyJ2IjoiMSJ9.zsxhfAGbbEC8wNYVFR-VftN6LpIjSurM735GiJ8oUWtT3mX1j50dmwXnjgzIlDD2OrhS3iDU11q10kV7nOMAY79v54DmeAuYWcO7VgkoAvTSaJBRIeAa62_T8uk6-KXQaIWnlXCs5aIBZR4QNFQy4mSZHhwrWvj9QIcuOwUW5d-53o5UCIPfcjszXloefJcs3dwUppM0pctsecHF3f1mhyDyvtEcps4baOcTtk9f5cI.TbmNiGvaID9kxpgBR3-0xO91EMYBEumhrq9pr0DKjEQ&dib_tag=se&keywords=intelligent+investor&qid=1790536958&s=books&sr=1-1"
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="hero-book-link"
              className="group mt-7 inline-flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:border-[#2F80ED]/40 hover:bg-white/[0.07] transition-colors"
            >
              <span className="w-9 h-9 shrink-0 rounded-lg bg-[#2F80ED]/15 grid place-items-center text-[#2F80ED]">
                <BookOpen className="w-4 h-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.15em] text-slate-400 font-num">Our recommended read</span>
                <span className="block text-sm font-semibold text-slate-100 truncate">The Intelligent Investor — Benjamin Graham</span>
              </span>
              <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#2F80ED] transition-colors" />
            </a>
          </div>

          {/* Right — mini dashboard preview + $150k badge */}
          <div className="md:col-span-6 min-w-0 relative reveal" style={{ animationDelay: "0.12s" }} data-testid="hero-dashboard-preview">
            <HeroPreview featured={featured} />
            <div className="hidden md:flex absolute -bottom-3 left-2 lg:-left-4 z-20 items-center gap-2 rounded-2xl bg-[#10B981] text-white px-4 py-2.5 shadow-xl shadow-[#10B981]/30 rotate-[-2deg]" data-testid="hero-virtual-funds-badge">
              <Coins className="w-6 h-6" />
              <span className="leading-none">
                <span className="block font-num font-extrabold text-xl">$150,000</span>
                <span className="block text-[10px] font-num uppercase tracking-[0.18em] text-white/90">Virtual Funds</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM COMPETITION */}
      <section data-testid="team-competition">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Who will claim the number one spot?</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Bring your competitive spirit to the stock market! Challenge your friends, classmates, club members, or family to a virtual trading competition. Test your strategies, learn about financial markets, and compete to climb the leaderboard.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-8">
          {teams.map((t) => (
            <div
              key={t.title}
              data-testid={`team-card-${t.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="rounded-2xl border border-border bg-card p-6 text-center hover:border-[#10B981]/50 hover:-translate-y-1 transition-[transform,border-color] duration-200"
            >
              <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-4 bg-[#10B981]/12 text-[#10B981]">
                <t.icon className="w-7 h-7" />
              </div>
              <h3 className="font-heading font-bold text-lg"><span className="mr-1">{t.emoji}</span>{t.title}</h3>
              <p className="text-muted-foreground text-sm mt-1.5 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-8">
          <Button
            size="lg"
            onClick={createTeam}
            data-testid="create-team-btn"
            className="rounded-full bg-[#2F80ED] hover:bg-[#256ECC] text-white text-base font-bold px-8 h-12 shadow-lg shadow-[#2F80ED]/30"
          >
            <Users className="w-5 h-5 mr-2" /> Create Your Team
          </Button>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="rounded-2xl border border-border bg-card p-6 hover:border-primary/40 hover:-translate-y-1 transition-[transform,border-color] duration-200"
            >
              <div className={`w-11 h-11 rounded-xl grid place-items-center mb-4 ${f.tint}`}>
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
              <p className="text-muted-foreground text-sm mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MARKET OVERVIEW + LEADERBOARD PREVIEW */}
      <section>
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Trade real markets, risk-free</h2>
                <p className="text-muted-foreground text-sm mt-1">Live US &amp; Canadian indices — the same prices you'll practise with.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {indices.map((ix) => (
                <div
                  key={ix.name}
                  data-testid={`overview-card-${ix.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:-translate-y-1 transition-[transform,border-color] duration-200"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-num uppercase tracking-wider text-muted-foreground">{ix.name}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-num font-semibold ${ix.up ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
                      {ix.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{ix.pct}
                    </span>
                  </div>
                  <div className="font-num font-bold text-2xl mt-3">{ix.value}</div>
                  <div className={`font-num text-sm mt-0.5 ${ix.up ? "text-[#10B981]" : "text-[#EF4444]"}`}>{ix.chg} ({ix.pct})</div>
                  <div className="h-10 mt-3"><Sparkline up={ix.up} /></div>
                </div>
              ))}
            </div>
          </div>
          <LeaderboardPreview onView={viewLeaderboard} />
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B1934] via-[#0E2044] to-[#08152B] px-6 sm:px-12 py-14 text-center relative overflow-hidden">
        <div className="pointer-events-none absolute -top-16 right-10 w-64 h-64 rounded-full bg-[#10B981]/15 blur-3xl" aria-hidden="true" />
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-50 tracking-tight relative">Ready to take the challenge?</h2>
        <p className="text-slate-300 mt-4 max-w-2xl mx-auto relative">Start with $150,000 in virtual trading funds. Test your strategy. Represent your team. Climb the leaderboard.</p>
        <Button
          size="lg"
          onClick={startTrading}
          data-testid="cta-start-trading-btn"
          className="rounded-full bg-[#10B981] hover:bg-[#0EA371] text-white text-base font-bold px-9 h-13 py-3.5 mt-7 shadow-lg shadow-[#10B981]/30 transition-colors relative"
        >
          🚀 Start Practice Trading <ArrowUpRight className="w-4 h-4 ml-1.5" />
        </Button>
        <div className="text-xs text-slate-400 font-num mt-4 relative">Virtual funds only — no real money is used or at risk.</div>
      </section>
    </div>
  );
}

function LeaderboardPreview({ onView }) {
  const rows = [
    { rank: 1, name: "Riverside High", ret: "+18.4%" },
    { rank: 2, name: "The Patels", ret: "+14.9%" },
    { rank: 3, name: "Campus Investors", ret: "+11.2%" },
    { rank: 4, name: "Team Momentum", ret: "+8.7%" },
    { rank: 5, name: "Weekend Traders", ret: "+6.1%" },
  ];
  const medal = (r) => (r === 1 ? "text-yellow-500" : r === 2 ? "text-slate-400" : r === 3 ? "text-amber-700" : "text-muted-foreground");
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="leaderboard-preview">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-5 h-5 text-yellow-500" />
        <h3 className="font-heading font-bold text-lg">Leaderboard</h3>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">Sample standings — sign up to see the live board.</p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.rank} className="flex items-center gap-3 rounded-lg bg-secondary/40 px-3 py-2">
            <span className={`w-6 text-center font-num font-extrabold ${medal(r.rank)}`}>
              {r.rank <= 3 ? <Crown className={`w-4 h-4 inline ${medal(r.rank)}`} /> : `#${r.rank}`}
            </span>
            <span className="flex-1 min-w-0 truncate font-semibold text-sm">{r.name}</span>
            <span className="font-num font-bold text-emerald-600">{r.ret}</span>
          </div>
        ))}
      </div>
      <Button onClick={onView} variant="outline" className="w-full mt-4 gap-1.5" data-testid="leaderboard-preview-btn">
        <Trophy className="w-4 h-4" /> View the Leaderboard
      </Button>
    </div>
  );
}

function PhoneMock() {
  const phoneWatch = [
    { s: "MSFT", p: "516.17", pct: "+0.35%", up: true },
    { s: "NVDA", p: "125.48", pct: "+3.47%", up: true },
    { s: "TSLA", p: "265.32", pct: "-0.43%", up: false },
    { s: "AMZN", p: "178.24", pct: "+1.49%", up: true },
  ];
  return (
    <div className="rounded-[1.4rem] bg-[#0a1220] p-1 ring-1 ring-white/10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8)]">
      <div className="rounded-[1.1rem] bg-[#08152B] overflow-hidden">
        <div className="flex items-center justify-between px-2.5 pt-1.5 pb-0.5 text-[6px] font-num text-slate-400">
          <span>9:41</span>
          <span className="flex items-center gap-0.5"><span className="w-2 h-1 rounded-[1px] bg-slate-400" /><span className="w-2 h-1.5 rounded-[1px] border border-slate-500" /></span>
        </div>
        <div className="flex items-center gap-1 px-2.5 pt-1">
          <span className="w-4 h-4 rounded-[4px] bg-red-600 grid place-items-center"><Activity className="w-2.5 h-2.5 text-white" strokeWidth={2.6} /></span>
          <span className="leading-none"><span className="block text-[6px] font-heading font-extrabold text-slate-100">STEWART CAP</span><span className="block text-[4px] font-num tracking-[0.25em] text-slate-400">FINANCE</span></span>
        </div>
        <div className="flex items-center gap-1 px-2.5 pt-1.5">
          {["US", "TSX", "Watchlist"].map((t, i) => (
            <span key={t} className={`rounded-full px-1.5 py-0.5 text-[6px] font-num ${i === 0 ? "bg-[#2F80ED] text-white" : "text-slate-400 border border-white/10"}`}>{t}</span>
          ))}
        </div>
        <div className="px-2.5 pt-1.5">
          <div className="text-[6.5px] font-num text-slate-400">AAPL · Apple Inc.</div>
          <div className="font-num font-extrabold text-base text-slate-50 leading-tight">184.92</div>
          <div className="font-num text-[7px] text-[#10B981]">+2.41 (+1.32%)</div>
          <div className="h-9 mt-1"><Sparkline up /></div>
          <div className="flex items-center gap-1 mt-1">
            {["1D", "1W", "1M", "1Y"].map((r, i) => (
              <span key={r} className={`rounded px-1 py-0.5 text-[5.5px] font-num ${i === 0 ? "bg-[#2F80ED] text-white" : "text-slate-400 border border-white/10"}`}>{r}</span>
            ))}
          </div>
          <div className="mt-1.5 space-y-1">
            {phoneWatch.map((w) => (
              <div key={w.s} className="flex items-center justify-between text-[6.5px] font-num">
                <span className="text-slate-200 font-semibold">{w.s}</span>
                <span className="text-slate-400">{w.p}</span>
                <span className={w.up ? "text-[#10B981]" : "text-[#EF4444]"}>{w.pct}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mx-2.5 mt-1.5 mb-2.5 rounded-md bg-[#2F80ED]/10 border border-[#2F80ED]/25 px-2 py-1">
          <div className="flex items-center gap-1 text-[6px] font-num text-[#2F80ED]"><Sparkles className="w-2 h-2" /> AI Market Insight</div>
          <div className="text-[6px] text-slate-400 leading-snug mt-0.5">AAPL up on strong iPhone sales &amp; a positive analyst upgrade.</div>
        </div>
      </div>
    </div>
  );
}

function HeroPreview({ featured }) {
  const nav = [
    ["Home", Home, true], ["Portfolio", Briefcase, false], ["Watchlist", Star, false], ["Markets", BarChart3, false],
    ["Movers", Radar, false], ["News", Newspaper, false], ["Brokers", Building2, false], ["Tools", Wrench, false],
  ];
  const ranges = ["1D", "1W", "1M", "3M", "1Y", "5Y"];
  const stats = [
    { label: "Advancers", value: "523", up: true },
    { label: "Decliners", value: "287", up: false },
    { label: "52W High", value: "186", up: true },
    { label: "52W Low", value: "64", up: false },
  ];
  const movers = [
    { s: "AAPL", p: "184.92", c: "+2.41", pct: "+1.32%", up: true },
    { s: "NVDA", p: "125.48", c: "+4.21", pct: "+3.47%", up: true },
    { s: "MSFT", p: "516.17", c: "+1.80", pct: "+0.35%", up: true },
  ];
  const factors = ["Higher than expected sales", "Analyst price target increase", "Strong market sentiment"];

  return (
    <div className="relative w-full">
      {/* Tablet & desktop (>=768px): iPad + overlapping phone */}
      <div className="hidden md:block relative mx-auto w-full pb-4">
        {/* restrained blue glow behind devices */}
        <div className="absolute inset-x-6 top-6 bottom-0 rounded-[40%] bg-[#2F80ED]/20 blur-3xl -z-0" aria-hidden="true" />
        {/* iPad / tablet */}
        <div className="relative z-0 rotate-[-1deg]">
          <div className="rounded-[1.4rem] bg-[#0a1220] p-1.5 lg:p-2 ring-1 ring-white/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.75)]">
            <div className="rounded-[1rem] bg-[#0B1934] overflow-hidden">
              {/* top bar */}
              <div className="flex items-center gap-2 px-2.5 py-2 border-b border-white/[0.06]">
                <span className="w-5 h-5 shrink-0 rounded-[5px] bg-red-600 grid place-items-center"><Activity className="w-3 h-3 text-white" strokeWidth={2.6} /></span>
                <span className="leading-none">
                  <span className="block text-[8px] font-heading font-extrabold text-slate-100 tracking-tight">STEWART CAP</span>
                  <span className="block text-[5px] font-num tracking-[0.25em] text-slate-400">FINANCE</span>
                </span>
                <span className="ml-2 flex-1 flex items-center gap-1 rounded-full bg-white/[0.06] px-2 py-1 text-[7px] text-slate-500 max-w-[150px]">
                  <Search className="w-2.5 h-2.5" /> Search stocks, ETFs…
                </span>
                <span className="ml-auto inline-flex items-center gap-1 text-[7px] font-num text-[#10B981]"><span className="w-1 h-1 rounded-full bg-[#10B981] live-dot" />LIVE</span>
              </div>

              <div className="flex">
                {/* sidebar */}
                <aside className="w-[20%] py-2 border-r border-white/[0.06] space-y-0.5">
                  {nav.map(([label, Icon, active]) => (
                    <div key={label} className={`flex items-center gap-1 px-2 py-1 text-[7px] font-medium ${active ? "text-white bg-[#2F80ED]/15 border-l-2 border-[#2F80ED]" : "text-slate-400"}`}>
                      <Icon className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{label}</span>
                    </div>
                  ))}
                </aside>

                {/* main center */}
                <div className="flex-1 min-w-0 p-2.5">
                  <div className="flex items-center gap-1 mb-1.5">
                    {["TSX", "NASDAQ", "S&P 500", "Watchlist"].map((t, i) => (
                      <span key={t} className={`rounded-full px-1.5 py-0.5 text-[6.5px] font-num ${i === 0 ? "bg-[#2F80ED] text-white" : "text-slate-400 border border-white/10"}`}>{t}</span>
                    ))}
                  </div>
                  <div className="text-[7px] font-num uppercase tracking-wider text-slate-400">{featured.name}</div>
                  <div className="font-num font-extrabold text-lg text-slate-50 leading-tight" data-testid="hero-featured-value">{featured.value}</div>
                  <div className={`font-num text-[9px] ${featured.up ? "text-[#10B981]" : "text-[#EF4444]"}`}>{featured.chg} ({featured.pct})</div>
                  <div className="h-12 mt-1"><Sparkline up={featured.up} /></div>
                  <div className="flex items-center gap-1 mt-1">
                    {ranges.map((r, i) => (
                      <span key={r} className={`rounded px-1 py-0.5 text-[6px] font-num ${i === 0 ? "bg-[#2F80ED] text-white" : "text-slate-400 border border-white/10"}`}>{r}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    {stats.map((st) => (
                      <div key={st.label} className="rounded-md border border-white/10 bg-[#08152B] px-1 py-1 text-center">
                        <div className="text-[5.5px] font-num uppercase tracking-wide text-slate-400 truncate">{st.label}</div>
                        <div className={`font-num font-bold text-[9px] ${st.up ? "text-[#10B981]" : "text-[#EF4444]"}`}>{st.value}</div>
                      </div>
                    ))}
                  </div>
                  {/* Top Movers */}
                  <div className="mt-2">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-[7px] font-heading font-semibold text-slate-200">Top Movers</span>
                      <span className="rounded px-1 py-0.5 text-[6px] font-num bg-[#2F80ED] text-white">Top Gainers</span>
                      <span className="text-[6px] font-num text-slate-500">Top Losers</span>
                      <span className="text-[6px] font-num text-slate-500">Most Active</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-1.5 gap-y-1 text-[6.5px] font-num">
                      <span className="text-slate-500">Symbol</span><span className="text-slate-500 text-right">Price</span><span className="text-slate-500 text-right">Chg</span><span className="text-slate-500 text-right">%</span>
                      {movers.map((m) => (
                        <React.Fragment key={m.s}>
                          <span className="text-slate-200 font-semibold">{m.s}</span>
                          <span className="text-slate-300 text-right">{m.p}</span>
                          <span className="text-[#10B981] text-right">{m.c}</span>
                          <span className="text-[#10B981] text-right">{m.pct}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                {/* AI panel */}
                <div className="w-[27%] p-2 border-l border-white/[0.06] bg-[#0A1730]">
                  <div className="inline-flex items-center gap-1 rounded-md bg-[#2F80ED]/15 px-1.5 py-0.5 text-[6.5px] font-num text-[#2F80ED]"><Sparkles className="w-2.5 h-2.5" /> AI Insight</div>
                  <div className="text-[8px] font-heading font-semibold text-slate-100 mt-1.5">Why did AAPL move?</div>
                  <p className="text-[6.5px] text-slate-400 leading-snug mt-1">AAPL is up 1.32% after strong iPhone sales and a positive analyst upgrade.</p>
                  <div className="text-[6.5px] font-semibold text-slate-300 mt-1.5">Key Factors</div>
                  <ul className="mt-0.5 space-y-0.5">
                    {factors.map((f) => (
                      <li key={f} className="flex items-start gap-1 text-[6px] text-slate-400"><span className="mt-[3px] w-0.5 h-0.5 rounded-full bg-[#2F80ED] shrink-0" />{f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* iPhone — in front, overlapping lower-right; less overlap on tablet */}
        <div className="absolute right-0 lg:-right-3 bottom-[-4%] w-[29%] lg:w-[33%] max-w-[190px] rotate-[3deg] z-10">
          <PhoneMock />
        </div>
      </div>

      {/* Mobile (<768px): phone only, centered below CTAs */}
      <div className="md:hidden w-full flex justify-center">
        <div className="relative w-[60vw] max-w-[230px] mt-1" data-testid="hero-phone-mobile">
          <div className="absolute inset-x-3 top-3 bottom-3 rounded-[45%] bg-[#2F80ED]/20 blur-2xl -z-0" aria-hidden="true" />
          <div className="relative rotate-[1.5deg]"><PhoneMock /></div>
        </div>
      </div>
    </div>
  );
}
